/**
 * Frontend Analytics Calculator for TrackerV2
 * 
 * Calculates all analytics metrics from raw screenshot data.
 * Follows architecture rules: ALL analytics in frontend, backend returns raw data only.
 * 
 * Uses:
 * - durationCalculator for smart gap detection
 * - TimezoneService for timezone handling
 * - DateService for date operations
 */

import { calculateDurationsWithGapDetection } from './durationCalculator';

export interface TopApp {
  name: string;
  count: number;
  hours: number;
  percentage: number;
  productivity?: 'high' | 'medium' | 'low';
}

export interface WorkType {
  name: string;
  count: number;
  hours: number;
  percentage: number;
}

export interface ProjectWorkType {
  projectName: string;
  totalHours: number;
  workTypes: {
    name: string;
    hours: number;
    percentage: number;
  }[];
}

/**
 * Calculate top applications from screenshots
 * Groups by app name and calculates total time using gap detection
 * 
 * @param screenshots - Array of screenshots
 * @param interval - User's screenshot interval (1, 5, 10, or 30 minutes)
 * @returns Array of top apps with usage stats
 */
export const calculateTopApps = (screenshots: any[], interval: number): TopApp[] => {
  if (!screenshots || screenshots.length === 0) {
    return [];
  }

  // Apply gap detection to get accurate durations
  const screenshotsWithDuration = calculateDurationsWithGapDetection(screenshots, interval);
  
  // Group by app and sum durations
  const appMinutes: { [key: string]: number } = {};
  
  screenshotsWithDuration.forEach(screenshot => {
    try {
      const apps = screenshot.aiAnalysis?.detectedApps || screenshot.detectedApplications || [];
      
      if (Array.isArray(apps)) {
        apps.forEach((app: string) => {
          if (app && typeof app === 'string' && app.trim() !== '') {
            const cleanApp = app.trim();
            appMinutes[cleanApp] = (appMinutes[cleanApp] || 0) + screenshot.calculatedDuration;
          }
        });
      }
    } catch (e) {
      console.warn('Failed to process apps for screenshot:', screenshot.id, e);
    }
  });
  
  const totalScreenshots = screenshots.length;
  
  // Calculate average productivity for each app
  const appProductivity: { [key: string]: { high: number; medium: number; low: number } } = {};
  
  screenshotsWithDuration.forEach(screenshot => {
    try {
      const apps = screenshot.aiAnalysis?.detectedApps || screenshot.detectedApplications || [];
      const productivity = (screenshot.aiAnalysis?.productivity || screenshot.productivity || 'medium').toLowerCase();
      
      if (Array.isArray(apps)) {
        apps.forEach((app: string) => {
          if (app && typeof app === 'string' && app.trim() !== '') {
            const cleanApp = app.trim();
            if (!appProductivity[cleanApp]) {
              appProductivity[cleanApp] = { high: 0, medium: 0, low: 0 };
            }
            appProductivity[cleanApp][productivity as 'high' | 'medium' | 'low']++;
          }
        });
      }
    } catch (e) {
      // Skip invalid screenshots
    }
  });
  
  // Convert to array and calculate percentages
  return Object.entries(appMinutes)
    .filter(([app]) => app && app !== 'undefined')
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([name, minutes]) => {
      // Determine average productivity level for this app
      const prodStats = appProductivity[name] || { high: 0, medium: 0, low: 0 };
      const totalOccurrences = prodStats.high + prodStats.medium + prodStats.low;
      
      let productivity: 'high' | 'medium' | 'low' = 'medium';
      if (totalOccurrences > 0) {
        const highPercent = (prodStats.high / totalOccurrences) * 100;
        const lowPercent = (prodStats.low / totalOccurrences) * 100;
        
        // If >60% high, mark as high. If >60% low, mark as low. Otherwise medium.
        if (highPercent > 60) {
          productivity = 'high';
        } else if (lowPercent > 60) {
          productivity = 'low';
        } else {
          productivity = 'medium';
        }
      }
      
      return {
        name,
        count: Math.round(minutes / interval),
        hours: Math.round((minutes / 60) * 100) / 100,
        percentage: totalScreenshots > 0 ? Math.round((minutes / interval / totalScreenshots) * 100) : 0,
        productivity
      };
    });
};

/**
 * Calculate work type distribution from screenshots
 * 
 * @param screenshots - Array of screenshots
 * @param interval - User's screenshot interval
 * @returns Array of work types with usage stats
 */
export const calculateWorkTypes = (screenshots: any[], interval: number): WorkType[] => {
  if (!screenshots || screenshots.length === 0) {
    return [];
  }

  const screenshotsWithDuration = calculateDurationsWithGapDetection(screenshots, interval);
  
  const workTypeMinutes: { [key: string]: number } = {};
  
  screenshotsWithDuration.forEach(screenshot => {
    try {
      const workType = screenshot.aiAnalysis?.workType || screenshot.workType || 'General';
      
      if (workType && typeof workType === 'string' && workType.trim() !== '') {
        const cleanWorkType = workType.trim();
        workTypeMinutes[cleanWorkType] = (workTypeMinutes[cleanWorkType] || 0) + screenshot.calculatedDuration;
      }
    } catch (e) {
      console.warn('Failed to process work type for screenshot:', screenshot.id, e);
    }
  });
  
  const totalMinutes = Object.values(workTypeMinutes).reduce((sum, mins) => sum + mins, 0);
  const totalScreenshots = screenshots.length;
  
  return Object.entries(workTypeMinutes)
    .filter(([type]) => type && type !== 'undefined')
    .sort(([,a], [,b]) => b - a)
    .map(([name, minutes]) => ({
      name,
      count: Math.round(minutes / interval),
      hours: Math.round((minutes / 60) * 100) / 100,
      percentage: totalMinutes > 0 ? Math.round((minutes / totalMinutes) * 100) : 0
    }));
};

/**
 * Calculate project work types - groups by project, then by work type
 * Shows which work types are done on each project
 * 
 * @param screenshots - Array of screenshots
 * @param interval - User's screenshot interval
 * @returns Array of projects with work type breakdown
 */
export const calculateProjectWorkTypes = (screenshots: any[], interval: number): ProjectWorkType[] => {
  if (!screenshots || screenshots.length === 0) {
    return [];
  }

  const screenshotsWithDuration = calculateDurationsWithGapDetection(screenshots, interval);
  
  // Group by project → workType → minutes
  const projectWorkTypes: { [project: string]: { [workType: string]: number } } = {};
  
  screenshotsWithDuration.forEach(screenshot => {
    try {
      const projectName = screenshot.aiAnalysis?.projectName || screenshot.projectName || 'Unknown Project';
      const workType = screenshot.aiAnalysis?.workType || screenshot.workType || 'General';
      
      if (!projectWorkTypes[projectName]) {
        projectWorkTypes[projectName] = {};
      }
      
      const cleanWorkType = workType.trim();
      projectWorkTypes[projectName][cleanWorkType] = 
        (projectWorkTypes[projectName][cleanWorkType] || 0) + screenshot.calculatedDuration;
    } catch (e) {
      console.warn('Failed to process project work type for screenshot:', screenshot.id, e);
    }
  });
  
  // Transform to array format
  return Object.entries(projectWorkTypes)
    .map(([projectName, workTypes]) => {
      const totalMinutes = Object.values(workTypes).reduce((sum, mins) => sum + mins, 0);
      const totalHours = totalMinutes / 60;
      
      const workTypesArray = Object.entries(workTypes)
        .sort(([,a], [,b]) => b - a)
        .map(([name, minutes]) => ({
          name,
          hours: Math.round((minutes / 60) * 100) / 100,
          percentage: Math.round((minutes / totalMinutes) * 100)
        }));
      
      return {
        projectName,
        totalHours: Math.round(totalHours * 100) / 100,
        workTypes: workTypesArray
      };
    })
    .sort((a, b) => b.totalHours - a.totalHours);
};

/**
 * Calculate productivity score from screenshots
 * High = 80, Medium = 60, Low = 30
 * 
 * @param screenshots - Array of screenshots
 * @param interval - User's screenshot interval
 * @returns Productivity score (0-100)
 */
export const calculateProductivityScore = (screenshots: any[], interval: number): number => {
  if (!screenshots || screenshots.length === 0) {
    return 0;
  }

  const screenshotsWithDuration = calculateDurationsWithGapDetection(screenshots, interval);
  
  let totalScore = 0;
  let count = 0;
  
  screenshotsWithDuration.forEach(screenshot => {
    try {
      const productivity = screenshot.aiAnalysis?.productivity || screenshot.productivity || 'medium';
      
      if (productivity === 'high') {
        totalScore += 80;
      } else if (productivity === 'medium') {
        totalScore += 60;
      } else {
        totalScore += 30;
      }
      
      count++;
    } catch (e) {
      // Skip invalid screenshots
    }
  });
  
  return count > 0 ? Math.round(totalScore / count) : 0;
};

/**
 * Calculate productive time (high + medium productivity screenshots)
 * 
 * @param screenshots - Array of screenshots
 * @param interval - User's screenshot interval
 * @returns Productive time in minutes
 */
export const calculateProductiveTime = (screenshots: any[], interval: number): number => {
  if (!screenshots || screenshots.length === 0) {
    return 0;
  }

  const productiveScreenshots = screenshots.filter(s => {
    const productivity = s.aiAnalysis?.productivity || s.productivity || 'medium';
    return productivity === 'high' || productivity === 'medium';
  });
  
  const screenshotsWithDuration = calculateDurationsWithGapDetection(productiveScreenshots, interval);
  
  return screenshotsWithDuration.reduce((sum, s) => sum + s.calculatedDuration, 0);
};
